import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchPublicVirtualNumberCountries,
  fetchPublicVirtualNumberPrices,
  setSelectedCountry,
  purchaseVirtualNumber,
  setPage
} from '../store/slices/virtualNumberSlice.js';
import { GlassCard } from '../components/ui/GlassCard.jsx';
import { CountryFlag } from '../components/ui/CountryFlag.jsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.jsx';
import { FiShoppingCart, FiSearch } from 'react-icons/fi';
import { getServiceIcon } from '../utils/serviceIcons.js';
import { sortVirtualNumberCountries } from '../utils/countryOrdering.js';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const NAIRA_RATE = 1500
export const VirtualNumbersPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    virtualNumberCountries,
    virtualNumberPrices,
    selectedCountry,
    pagination,
    isLoading,
    purchaseLoading
  } = useSelector((state) => state.virtualNumbers);
  const { user } = useSelector((state) => state.auth);

  const [countrySearchInput, setCountrySearchInput] = useState('');
  const [serviceSearchInput, setServiceSearchInput] = useState('');
  const [serviceSearch, setServiceSearch] = useState('');
  const [confirmingService, setConfirmingService] = useState(null);
  const [purchasingServiceCode, setPurchasingServiceCode] = useState(null);
  const [purchaseError, setPurchaseError] = useState('');
  const [showVideoModal, setShowVideoModal] = useState(false);

  // Handle service search with automatic space trimming
  const handleServiceSearchChange = (value) => {
    // Remove all leading/trailing spaces automatically
    const trimmed = value.trim();
    setServiceSearchInput(trimmed);
  };

  // Country search: keep user input intact (allow spaces); trim only when filtering
  const handleCountrySearchChange = (value) => {
    setCountrySearchInput(value);
  };

  const formatNaira = (amount) => {
    return `₦${(amount * NAIRA_RATE).toLocaleString('en-NG')}`;
  };

  const filteredCountries = useMemo(() => {
    const normalizedQuery = countrySearchInput.trim().toLowerCase();
    const filtered = normalizedQuery
      ? virtualNumberCountries.filter((country) =>
          country.countryName.toLowerCase().includes(normalizedQuery) ||
          country.countryCode?.toLowerCase().includes(normalizedQuery)
        )
      : virtualNumberCountries;

    const sorted = sortVirtualNumberCountries(filtered);
    if (selectedCountry && !sorted.some((country) => country._id === selectedCountry)) {
      const selected = virtualNumberCountries.find((country) => country._id === selectedCountry);
      return selected ? [selected, ...sorted] : sorted;
    }

    return sorted;
  }, [virtualNumberCountries, countrySearchInput, selectedCountry]);

  const selectedCountryObj = useMemo(
    () => virtualNumberCountries.find((country) => country._id === selectedCountry),
    [virtualNumberCountries, selectedCountry]
  );

  useEffect(() => {
    dispatch(fetchPublicVirtualNumberCountries());
  }, [dispatch]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setServiceSearch(serviceSearchInput);
      dispatch(setPage(1));
    }, 300);

    return () => clearTimeout(handler);
  }, [serviceSearchInput, dispatch]);

  useEffect(() => {
    if (selectedCountry) {
      dispatch(fetchPublicVirtualNumberPrices({
        countryId: selectedCountry,
        page: pagination.page,
        limit: pagination.limit,
        serviceSearch
      }));
    }
  }, [selectedCountry, pagination.page, pagination.limit, serviceSearch, dispatch]);

  const handlePurchaseClick = (service) => {
    if (purchaseLoading || purchasingServiceCode) return;
    setPurchaseError('');
    setConfirmingService(service);
  };

  const handleConfirmPurchase = async () => {
    if (!confirmingService) return;
    const service = confirmingService;
    const priceNaira = service.adminSellingPrice * NAIRA_RATE;

    if ((user?.balance || 0) < priceNaira) {
      toast.error('Insufficient balance. Please deposit funds.');
      setConfirmingService(null);
      return;
    }

    setPurchasingServiceCode(service.serviceCode);

    try {
      const result = await dispatch(purchaseVirtualNumber({
        serviceCode: service.serviceCode,
        countryId: selectedCountry
      })).unwrap();

      console.log('Purchase response:', result);
      setConfirmingService(null);
      navigate('/my-numbers');
    } catch (error) {
      console.error('Purchase failed:', error);
      const message =
        error?.message ||
        error?.payload?.message ||
        (typeof error === 'string' ? error : 'Purchase failed. Please try again.');
      setPurchaseError(message);
      setConfirmingService(null);
    } finally {
      setPurchasingServiceCode(null);
    }
  };

  const handleCancelPurchase = () => {
    setConfirmingService(null);
  };

  const openVideoModal = () => setShowVideoModal(true);
  const closeVideoModal = () => setShowVideoModal(false);

  return (
    <div className="min-h-screen bg-luxury-gradient p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold mb-2">Buy Virtual Numbers</h1>
              <p className="text-gray-400">Select a country and choose your virtual number service</p>
            </div>
            <button
              type="button"
              onClick={openVideoModal}
              className="px-4 py-2 rounded-lg border border-white/20 bg-secondary-600 text-white hover:bg-secondary-900 cursor-pointer transition-colors"
            >
              Watch how to buy
            </button>
          </div>
        </div>

        {purchaseError && (
          <div className="mb-6 rounded-3xl border border-rose-200 bg-rose-50 p-4 text-rose-800">
            <p className="font-semibold">No number available for that selection.</p>
            <p className="mt-2 text-sm">{purchaseError}</p>
          </div>
        )}

        {/* Country selector */}
        {virtualNumberCountries.length > 0 && (
          <GlassCard className="p-6 mb-8">
            <div className="grid gap-4 md:grid-cols-[1fr_auto] items-end">
              <div>
                <label className="block text-sm font-medium mb-3">Search Countries</label>
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    placeholder="Search countries..."
                    value={countrySearchInput}
                    onChange={(e) => handleCountrySearchChange(e.target.value)}
                    className="input-luxury w-full pl-10"
                  />
                </div>
              </div>
              {selectedCountryObj && (
                <div className="text-right">
                  <p className="text-sm text-gray-500">Selected country</p>
                  <p className="mt-2 text-xl font-semibold flex items-center gap-3">
                    <CountryFlag countryName={selectedCountryObj.countryName} countryCode={selectedCountryObj.countryCode} className="h-7 w-11 rounded-md object-cover" />
                    {selectedCountryObj.countryName}
                  </p>
                </div>
              )}
            </div>

            <label className="block text-sm font-medium mb-3 mt-6">Choose Country</label>
            <select
              value={selectedCountry || ''}
              onChange={(e) => dispatch(setSelectedCountry(Number(e.target.value)))}
              className="input-luxury w-full md:w-96"
            >
              {filteredCountries.map((country) => (
                <option key={country._id} value={country._id}>
                  {country.countryName} ({country.enabledCount} services)
                </option>
              ))}
            </select>
            <div className="mt-4 max-h-64 overflow-y-auto rounded-3xl border border-gray-200 bg-white shadow-sm">
              {filteredCountries.length > 0 ? (
                filteredCountries.map((country) => (
                  <button
                    key={country._id}
                    type="button"
                    onClick={() => dispatch(setSelectedCountry(country._id))}
                    className="w-full text-left px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-3 mb-1">
                      <CountryFlag countryCode={country.countryCode} countryName={country.countryName}  className="h-7 w-7 rounded-md object-cover" />
                      <div className="font-bold text-gray-900">{country.countryName}</div>
                    </div>
                    {/* <div className="text-sm text-gray-500">{country.countryCode.toUpperCase()} · {country.enabledCount} services</div> */}
                  </button>
                ))
              ) : (
                <div className="px-4 py-3 text-sm text-gray-500">No countries match your search.</div>
              )}
            </div>
          </GlassCard>
        )}

        {/* Service search */}
        {selectedCountry && (
          <GlassCard className="p-6 mb-8">
            <label className="block text-sm font-medium mb-3">Search Services</label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search by service name or code (spaces auto-trimmed)..."
                value={serviceSearchInput}
                onChange={(e) => handleServiceSearchChange(e.target.value)}
                className="input-luxury w-full pl-10"
              />
            </div>
          </GlassCard>
        )}

         {user && (
           <GlassCard className="p-4 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
             <div>
               <p className="text-sm text-gray-500">Wallet Balance</p>
               <p className="text-2xl font-semibold">₦{user.balance?.toLocaleString() || 0}</p>
             </div>
             <div className="flex items-center gap-3">
               <span className="text-sm text-gray-400">Available for purchases</span>
               <button
                 type="button"
                 onClick={() => navigate('/deposit')}
                 className="px-4 py-2 rounded-lg bg-secondary-600 text-white hover:bg-secondary-700 transition-colors"
               >
                 Deposit
               </button>
             </div>
           </GlassCard>
         )}
        {isLoading ? (
          <GlassCard className="p-12 text-center">
            <p className="text-gray-400">Loading services...</p>
          </GlassCard>
        ) : virtualNumberPrices.length === 0 ? (
          <GlassCard className="p-12 text-center">
            <p className="text-gray-400">
              {serviceSearch ? 'No services match your search' : 'No services available for this country'}
            </p>
          </GlassCard>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {virtualNumberPrices.map((service) => {
                const Icon = getServiceIcon(service.serviceName, service.serviceCode);
                return (
                  <GlassCard
                    key={service._id}
                    className="p-4 hover:shadow-lg transition-shadow"
                  >
                    <div className="flex flex-col justify-between h-full">
                      <div className="mb-4">
                        <div className="flex items-center justify-between gap-3 mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl text-primary-600"><Icon /></span>
                            <h3 className="text-lg font-semibold">{service.serviceName}</h3>
                          </div>
                          <span className="inline-flex items-center">
                            <CountryFlag countryCode={service.countryCode} countryName={service.countryName}  className="h-6 w-9 rounded-sm object-cover" />
                          </span>
                        </div>
                        {/* <p className="text-sm text-gray-400 mb-2">Code: {service.serviceCode}</p> */}
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>Price:</span>
                            <span className="font-bold text-primary-400">
                              {formatNaira(service.adminSellingPrice)}
                            </span>
                          </div>
                          {/* intentionally hide sold out status per UX request */}
                        </div>
                      </div>
                      {(() => {
                        const priceNaira = service.adminSellingPrice * NAIRA_RATE;
                        const insufficient = (user?.balance || 0) < priceNaira;
                        const isPurchasing = purchasingServiceCode === service.serviceCode && purchaseLoading;
                        return (
                          <button
                            onClick={() => handlePurchaseClick(service)}
                            disabled={insufficient || purchaseLoading}
                            className="w-full flex items-center justify-center space-x-2 bg-primary-600 text-white py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <FiShoppingCart size={18} />
                            {isPurchasing ? (
                              <span>Purchasing...</span>
                            ) : insufficient ? (
                              <span>Insufficient</span>
                            ) : (
                              <span>Purchase</span>
                            )}
                          </button>
                        );
                      })()}
                    </div>
                  </GlassCard>
                );
              })}
            </div>

            {pagination.pages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-8">
                <div className="text-sm text-gray-500">
                  Showing page {pagination.page} of {pagination.pages} ({pagination.total} services)
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => dispatch(setPage(Math.max(1, pagination.page - 1)))}
                    disabled={pagination.page === 1}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ← Previous
                  </button>
                  <button
                    onClick={() => dispatch(setPage(Math.min(pagination.pages, pagination.page + 1)))}
                    disabled={pagination.page === pagination.pages}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showVideoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-2xl rounded-3xl border border-gray-800 bg-slate-950 p-6 text-white shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-2xl font-semibold">Video coming soon</h2>
                <p className="mt-2 text-sm text-gray-400">We're preparing a short guide to help you buy virtual numbers.</p>
              </div>
              <button
                type="button"
                onClick={closeVideoModal}
                className="text-2xl font-semibold text-gray-400 hover:text-white"
                aria-label="Close video modal"
              >
                ×
              </button>
            </div>
            <div className="mt-6 rounded-3xl border border-gray-800 bg-slate-900 p-6 text-center">
              <p className="text-lg font-semibold text-white">Video coming soon</p>
              <p className="mt-2 text-sm text-gray-400">A how-to video will be available here shortly.</p>
            </div>
            <div className="mt-6 text-right">
              <button
                type="button"
                onClick={closeVideoModal}
                className="inline-flex items-center justify-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!confirmingService}
        title="Confirm purchase"
        message={
          confirmingService
            ? `Are you sure you want to purchase ${confirmingService.serviceName} (${confirmingService.serviceCode}) for ${formatNaira(confirmingService.adminSellingPrice)} in ${selectedCountryObj?.countryName || 'selected country'}?`
            : ''
        }
        onConfirm={handleConfirmPurchase}
        onCancel={handleCancelPurchase}
        confirmText="Purchase"
        cancelText="Cancel"
        isDangerous={false}
        isLoading={purchaseLoading && !!confirmingService}
      />
    </div>
  );
};
